import { useContext, useEffect, useState } from 'react';

import { InventoryContext } from '@/contexts/InventoryContext';
import { getFavoritesChannel } from '@/tools/broadcast';
import { getDefaultImageUrl, getUniqueInventoryImageUrl } from '@/tools/inventory';
import { IMessageFavorites, MessageFavoritesType } from '@/types/inventory';
import { getMinimalInventory } from '@lamesarv-sdk/tools';
import { IBasicInventory, IInventory, InventoryField } from '@lamesarv-sdk/types';

interface UseIsFavoriteOptions {
  customImage?: string;
}

export const useIsFavorite = (item: IBasicInventory, { customImage }: UseIsFavoriteOptions = {}) => {
  const { context, favoriteSelectInventory, favoriteUnselectInventory } = useContext(InventoryContext);
  const [isFavorite, setIsFavorite] = useState<boolean>(false);

  const toggleFavorite = () => {
    const channelFavorites = getFavoritesChannel();

    if (!isFavorite) {
      const minimalItem = getMinimalInventory(
        item as IInventory,
        getDefaultImageUrl(item),
        getUniqueInventoryImageUrl(item, customImage, true),
      );
      const newFavorites = favoriteSelectInventory(minimalItem, true);
      channelFavorites.postMessage({
        type: MessageFavoritesType.favoriteSelectInventory,
        id: item[InventoryField.id],
        quantity: newFavorites.length,
      } as IMessageFavorites);
      return;
    }

    const newFavorites = favoriteUnselectInventory(item[InventoryField.id], true);
    channelFavorites.postMessage({
      type: MessageFavoritesType.favoriteUnselectInventory,
      id: item[InventoryField.id],
      quantity: newFavorites.length,
    } as IMessageFavorites);
  };

  useEffect(() => {
    const channel = getFavoritesChannel();

    channel.onmessage = (message: IMessageFavorites) => {
      if (message.id === item[InventoryField.id]) {
        setIsFavorite(message.type === MessageFavoritesType.favoriteSelectInventory);
      }
    };

    return () => {
      channel.close();
    };
  }, [item]);

  useEffect(() => {
    setIsFavorite(!!context?.inventoryFavorites?.find((favorite) => favorite.id === item[InventoryField.id]));
  }, [context.inventoryFavorites, item]);

  return {
    toggleFavorite,
    isFavorite,
  };
};
